import { mountCoreVueApp } from '@/entrypoints/helpers/core-vue-app';
import DomainMismatchNudge from '@/shared/components/UI/DomainMismatchNudge.vue';
import { userCookies } from '@/shared/constants/constants';
import { queryCountryConfigs } from '@/shared/graphql/gateway/queries';
import { parseConsentCookie } from '@/shared/pinia/modules/posthog/consent';
import { isPostHogEnabled } from '@/shared/pinia/modules/posthog/sdk';
import {
  trackDomainMismatchDetectionFailed,
  trackDomainMismatchNudgeShown
} from '@/shared/tracking/domain-mismatch-nudge';
import { apolloClientInit } from '@/shared/utils/apollo-client';
import { getCookie } from '@/shared/utils/cookie';
import { buildCountrySwitchUrl } from '@/shared/utils/country-switch-url';
import { checkResourceExistsOnStore } from '@/shared/utils/deep-link-equivalence';
import { detectVisitorCountryCode } from '@/shared/utils/domain-detection';
import { notifyBugsnagWithContext } from '@/shared/utils/error';
import { parseShopifyResourcePath } from '@/shared/utils/shopify-resource-path';
import { stripMarketPathPrefix } from '@/shared/utils/urls';

const mountSelector = '#domain-mismatch-nudge';

export const initDomainMismatchNudge = async (): Promise<void> => {
  // Four locales serve every route under a market path prefix, so the prefix
  // must come off before any root-relative comparison below.
  const pathname = stripMarketPathPrefix(window.location.pathname);

  if (pathname.startsWith('/cart')) {
    return;
  }

  if (getCookie(userCookies.DOMAIN_MISMATCH_SUPPRESSED)) {
    return;
  }

  // Scoped to visitors PostHog can track (also off in the theme editor).
  // Showing it to non-consented visitors is a product decision, not a default.
  if (!isPostHogEnabled() || !parseConsentCookie()) {
    return;
  }

  const mountElement = document.querySelector<HTMLDivElement>(mountSelector);

  if (!mountElement) {
    return;
  }

  try {
    const detectedCountryCode = await detectVisitorCountryCode();

    if (!detectedCountryCode) {
      trackDomainMismatchDetectionFailed({
        current_domain: window.location.hostname,
        failure_reason: 'no_country_detected'
      });
      return;
    }

    const graphqlHost = import.meta.env.VITE_HOBBII_GATEWAY;
    const apolloClient = apolloClientInit({ graphqlHost });
    const countryDataResponse = await apolloClient.query(queryCountryConfigs());
    const countryConfigs = countryDataResponse.data?.countryConfigs ?? [];

    const detectedConfig = countryConfigs.find(
      (config) => config?.countryCode === detectedCountryCode
    );

    // A detected country with no matching config is outside the ticket's
    // domain map (AU/JP/HK/NZ etc, "no redirect logic") - a legitimate
    // no-op, not a detection failure.
    if (!detectedConfig?.shopifyConfig?.storeEndpoint) {
      return;
    }

    const currentStoreName = window.Shopify.shop.split('.')[0];
    const detectedStoreName = detectedConfig.shopifyConfig.storefront?.name;

    // No storefront.name means we can't tell store identity apart from
    // domain identity - safer to skip than risk a false-positive nudge.
    if (!detectedStoreName || detectedStoreName === currentStoreName) {
      return;
    }

    const suggestedDomain = new URL(detectedConfig.shopifyConfig.storeEndpoint)
      .hostname;

    // Preserving the current path is only safe once confirmed to exist on
    // the target store - checked via its own Storefront API (CORS-open,
    // unlike the plain .json resource endpoints), using the public token
    // countryConfigs already returns for it. The homepage needs no check and
    // still carries its query string (utm tags and the like) across.
    const isHomepage = pathname === '/';
    let preservePath = isHomepage;

    if (!isHomepage) {
      const resource = parseShopifyResourcePath(pathname);
      const storefront = detectedConfig.shopifyConfig.storefront;

      if (resource && storefront?.endpoint && storefront?.publicAccessToken) {
        preservePath = await checkResourceExistsOnStore(
          storefront.endpoint,
          storefront.publicAccessToken,
          resource
        );
      }
    }

    const targetUrl = buildCountrySwitchUrl(
      detectedConfig.shopifyConfig.storeEndpoint,
      detectedConfig.countryCode ?? detectedCountryCode,
      { preservePath }
    );

    await mountCoreVueApp(
      DomainMismatchNudge,
      mountSelector,
      {
        currentDomain: window.location.hostname,
        suggestedDomain,
        targetUrl,
        fallbackToHomepage: !preservePath
      },
      null,
      { noClients: true }
    );

    trackDomainMismatchNudgeShown({
      current_domain: window.location.hostname,
      detected_country: detectedCountryCode,
      suggested_domain: suggestedDomain
    });
  } catch (error) {
    // Must never break the storefront - log and stop, no nudge.
    notifyBugsnagWithContext('initDomainMismatchNudge', error);
  }
};
